import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMyNav } from "@/hooks/useMyNav";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { usePlatformSettings } from "@/hooks/usePlatformSettings";
import { TierBadge } from "@/components/billing/TierBadge";
import { House, Settings, HelpCircle, CreditCard, LogOut, Loader2 } from "lucide-react";

const UserMenu = () => {
  const { user, signOut, loading } = useAuth();
  const { nav } = useMyNav();
  const { pricingEnabled } = usePlatformSettings();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin");
  };

  if (!user) return null;

  const name = user.user_metadata?.full_name || user.user_metadata?.name;
  const initials = name
    ? name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
    : (user.email || "").slice(0, 2).toUpperCase();
  const displayName = name || user.email?.split("@")[0] || "User";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-9 w-9 rounded-full">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 bg-popover border border-border shadow-lg z-50" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium leading-none truncate">{displayName}</p>
              {nav.is_manager && <TierBadge />}
            </div>
            <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/home" className="flex items-center gap-2 cursor-pointer">
            <House className="h-4 w-4" />
            Home
          </Link>
        </DropdownMenuItem>
        {nav.is_manager && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/settings/brand" className="flex items-center gap-2 cursor-pointer">
                <Settings className="h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/how-it-works" className="flex items-center gap-2 cursor-pointer">
                <HelpCircle className="h-4 w-4" />
                How It Works
              </Link>
            </DropdownMenuItem>
            {pricingEnabled && <DropdownMenuItem asChild>
              <Link to="/settings/billing" className="flex items-center gap-2 cursor-pointer">
                <CreditCard className="h-4 w-4" />
                Billing
              </Link>
            </DropdownMenuItem>}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          disabled={loading}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
